import { useState, useEffect, useMemo } from 'react';
import { LearningProgress, LearningTrack, LearningPathway } from '@/types/learningPathTypes';
import { useSupabaseAuth } from '@/hooks/auth';
import { getRecommendedNextCourses, canAccessCourse } from '@/data/courseHierarchy';
import { ALL_LEARNING_TRACKS } from '@/data/learningTracks';

export const useLearningProgress = () => {
  const { user, userModuleProgress } = useSupabaseAuth();
  const [learningProgress, setLearningProgress] = useState<LearningProgress[]>([]);
  const [loading, setLoading] = useState(true);

  // Get completed courses from user progress
  const completedCourses = useMemo(() => {
    if (!userModuleProgress) return [];
    
    // Extract unique course IDs from completed modules (100% progress)
    const completed = userModuleProgress
      .filter(progress => progress.progress === 100)
      .map(progress => {
        // Extract course ID from module ID (format: courseId-module-X)
        const parts = progress.module_id.split('-module-');
        return parts[0];
      });
    
    return [...new Set(completed)];
  }, [userModuleProgress]);

  // Calculate progress for each track
  const trackProgress = useMemo(() => {
    return ALL_LEARNING_TRACKS.map(track => {
      const totalPathways = track.pathways.length;
      let completedPathways = 0;
      let currentPathway = track.pathways[0]?.id || '';

      // Calculate completed pathways
      for (const pathway of track.pathways) {
        const pathwayCompleted = pathway.courseIds.every(courseId => 
          completedCourses.includes(courseId)
        );
        
        if (pathwayCompleted) {
          completedPathways++;
        } else if (!currentPathway || currentPathway === track.pathways[0]?.id) {
          // Set current pathway to first incomplete one
          currentPathway = pathway.id;
        }
      }

      const overallProgress = totalPathways > 0 ? (completedPathways / totalPathways) * 100 : 0;

      // Get skills acquired from completed pathways
      const skillsAcquired = track.pathways
        .slice(0, completedPathways)
        .flatMap(pathway => pathway.skills);

      return {
        userId: user?.id || '',
        trackId: track.id,
        currentPathway,
        completedPathways: track.pathways.slice(0, completedPathways).map(p => p.id),
        unlockedCourses: getUnlockedCoursesForTrack(track, completedCourses),
        skillsAcquired,
        overallProgress: Math.round(overallProgress),
        timeSpent: 0 // Would need to track this separately
      } as LearningProgress;
    });
  }, [ALL_LEARNING_TRACKS, completedCourses, user?.id]);

  // Get recommended next courses
  const recommendedCourses = useMemo(() => {
    return getRecommendedNextCourses(completedCourses);
  }, [completedCourses]);

  // Get next pathway for a specific track
  const getNextPathway = (trackId: string): LearningPathway | null => {
    const track = ALL_LEARNING_TRACKS.find(t => t.id === trackId);
    if (!track) return null;

    const progress = trackProgress.find(p => p.trackId === trackId);
    if (!progress) return null;

    return track.pathways.find(p => p.id === progress.currentPathway) || null;
  };

  // Check if user can access a specific course
  const canUserAccessCourse = (courseId: string): boolean => {
    return canAccessCourse(courseId, completedCourses);
  };

  // Get user's enrollment status for tracks
  const getTrackEnrollmentStatus = (trackId: string): 'not-enrolled' | 'in-progress' | 'completed' => {
    const progress = trackProgress.find(p => p.trackId === trackId);
    if (!progress) return 'not-enrolled';
    
    if (progress.overallProgress === 100) return 'completed';
    if (progress.overallProgress > 0) return 'in-progress';
    return 'not-enrolled';
  };

  useEffect(() => {
    if (user && userModuleProgress !== undefined) {
      setLearningProgress(trackProgress);
      setLoading(false);
    }
  }, [user, userModuleProgress, trackProgress]);

  return {
    learningProgress,
    trackProgress,
    completedCourses,
    recommendedCourses,
    loading,
    getNextPathway,
    canUserAccessCourse,
    getTrackEnrollmentStatus
  };
};

// Helper function to get unlocked courses for a track
function getUnlockedCoursesForTrack(track: LearningTrack, completedCourses: string[]): string[] {
  const unlockedCourses: string[] = [];

  for (const pathway of track.pathways) {
    for (const courseId of pathway.courseIds) {
      if (canAccessCourse(courseId, completedCourses)) {
        unlockedCourses.push(courseId);
      }
    }
  }

  return [...new Set(unlockedCourses)];
}